import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Camera, Loader, AlertCircle, Keyboard, RefreshCw, CheckCircle, Scan } from 'lucide-react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { NotFoundException } from '@zxing/library';
/**
 * WHY THIS VERSION FIXES THE "camera opens but never scans" ISSUE
 * ------------------------------------------------------------------
 * The previous implementation used `react-barcode-scanner`, which detects
 * codes via the experimental Barcode Detection API (native, or polyfilled
 * with a WASM build fetched at runtime). If that WASM asset fails to load
 * (CDN blocked, CSP, bundler didn't copy it, wrong MIME type, etc.) the
 * camera preview still works perfectly — getUserMedia has nothing to do
 * with detection — but decoding silently never happens. This is true of
 * almost every thin wrapper library built on that same API, which is why
 * switching packages didn't help.
 *
 * @zxing/browser (built on the pure-JS @zxing/library) does NOT use that
 * API. It grabs frames directly from a <video> element and decodes them
 * in JS, so there's no WASM fetch and no dependency on browser support
 * for BarcodeDetector. It also requires the <video> element to actually
 * be playing frames, which is why `playsInline`, `muted`, and `autoPlay`
 * are set explicitly below — without them iOS Safari will keep the
 * camera "open" but never actually render frames to decode.
 *
 * Install:
 *   npm install @zxing/browser @zxing/library
 */

const BarcodeScanner = ({
  onScan,
  onClose,
  title = 'Scan Barcode',
  description = 'Use hardware scanner or camera to scan barcode',
  autoFocus = true,
  enableHardware = true,
  enableCamera = true,
  enableHaptics = true
}) => {
  // State
  const [scanMode, setScanMode] = useState('hardware');
  const [manualInput, setManualInput] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hardwareActive, setHardwareActive] = useState(true);
  const [facingMode, setFacingMode] = useState('environment');

  // Refs
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const streamRef = useRef(null);
  const bufferRef = useRef('');
  const timeoutRef = useRef(null);
  const lastKeyTimeRef = useRef(0);
  const isScanningRef = useRef(false);
  const scanTimeoutRef = useRef(null);

  // ==================== HAPTIC FEEDBACK HELPER ====================
  const triggerHapticFeedback = useCallback(() => {
    if (!enableHaptics) return;
    try {
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }
    } catch (e) {
      console.warn('Haptic feedback not available or blocked:', e);
    }
  }, [enableHaptics]);

  // ==================== HANDLE SCAN COMPLETE ====================
  const handleScanComplete = useCallback((barcode) => {
    if (!barcode || barcode.trim() === '' || isScanningRef.current) return;

    isScanningRef.current = true;
    const trimmedBarcode = barcode.trim();
    console.log('✅ Barcode scanned:', trimmedBarcode);

    triggerHapticFeedback();

    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }

    scanTimeoutRef.current = setTimeout(() => {
      onScan(trimmedBarcode);
      setTimeout(() => {
        if (onClose) onClose();
      }, 300);
    }, 150);
  }, [onScan, onClose, triggerHapticFeedback]);

  // ==================== STOP CAMERA / DECODER HELPER ====================
  const stopCamera = useCallback(() => {
    try {
      codeReaderRef.current?.reset();
    } catch (e) {
      // ignore
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  // ==================== CAMERA SCANNER LOGIC (zxing) ====================
  useEffect(() => {
    if (scanMode !== 'camera' || !enableCamera) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const codeReader = new BrowserMultiFormatReader();
    codeReaderRef.current = codeReader;

    const start = async () => {
      try {
        // Ask for a specific facing mode; fall back to any camera if that
        // exact constraint isn't available on this device.
        const constraints = {
          video: { facingMode: { ideal: facingMode } },
          audio: false
        };

        const controls = await codeReader.decodeFromConstraints(
          constraints,
          videoRef.current,
          (result, err) => {
            if (cancelled) return;

            if (result) {
              handleScanComplete(result.getText());
            }
            // NotFoundException fires continuously between frames where no
            // barcode is visible — that's expected, not an error.
            if (err && !(err instanceof NotFoundException)) {
              console.warn('Decode error:', err);
            }
          }
        );

        if (cancelled) {
          controls.stop();
          return;
        }

        // Track the underlying MediaStream so we can fully release the
        // camera when switching modes or unmounting.
        const stream = videoRef.current?.srcObject;
        if (stream) streamRef.current = stream;

        setIsLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Camera stream error:', err);
        setIsLoading(false);
        if (err && (err.name === 'NotAllowedError' || err.message?.includes('permission'))) {
          setError('Camera permission denied. Please allow access in browser settings.');
        } else if (err && err.name === 'NotFoundError') {
          setError('No camera found on this device.');
        } else {
          setError('Unable to start camera. Please ensure permissions are granted and camera is available.');
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [scanMode, enableCamera, facingMode, handleScanComplete, stopCamera]);

  // Release the camera on unmount no matter what mode we're in.
  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  // ==================== HARDWARE SCANNER LOGIC ====================
  useEffect(() => {
    if (!enableHardware || scanMode !== 'hardware') return;

    const handleKeyDown = (event) => {
      const target = event.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        return;
      }

      const key = event.key;
      const currentTime = Date.now();
      const timeDiff = currentTime - lastKeyTimeRef.current;
      lastKeyTimeRef.current = currentTime;

      if (key === 'Enter') {
        if (bufferRef.current.trim()) {
          event.preventDefault();
          const barcode = bufferRef.current.trim();
          handleScanComplete(barcode);
          bufferRef.current = '';
          setHardwareActive(true);
        }
        return;
      }

      if (key.length === 1 && key.match(/[a-zA-Z0-9\-_./]/)) {
        bufferRef.current += key;
        setHardwareActive(true);

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        const isRapidScan = timeDiff < 50 || bufferRef.current.length > 3;
        const delay = isRapidScan ? 80 : 400;

        timeoutRef.current = setTimeout(() => {
          if (bufferRef.current.trim().length >= 3) {
            const barcode = bufferRef.current.trim();
            handleScanComplete(barcode);
          }
          bufferRef.current = '';
        }, delay);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [scanMode, enableHardware, handleScanComplete]);

  // ==================== UI HANDLERS ====================
  const toggleCamera = () => {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) {
      handleScanComplete(manualInput.trim());
      setManualInput('');
    }
  };

  const switchToHardware = () => {
    stopCamera();
    setScanMode('hardware');
    setError(null);
    setHardwareActive(true);
    setIsLoading(false);
    setManualInput('');
    bufferRef.current = '';
    isScanningRef.current = false;
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const switchToCamera = () => {
    setScanMode('camera');
    setError(null);
    setIsLoading(true);
    setManualInput('');
    bufferRef.current = '';
    isScanningRef.current = false;
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-xs p-0 sm:p-4 transition-all">
      <div className="w-full sm:max-w-lg bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col transition-all">

        {/* Mobile Swipe Handle */}
        <div className="sm:hidden w-full flex items-center justify-center pt-2.5 pb-1">
          <div className="w-10 h-1 rounded-full bg-slate-300 dark:bg-gray-600" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100 dark:border-gray-700">
          <div className="flex items-center space-x-2 min-w-0">
            {scanMode === 'hardware' ? (
              <Keyboard className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0" />
            ) : (
              <Camera className="h-5 w-5 text-purple-600 dark:text-purple-400 shrink-0" />
            )}
            <div>
              <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">
                {title}
              </h3>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate hidden sm:block">
                {description}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Mode Selector */}
        <div className="p-3 sm:p-4 border-b border-slate-100 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-900/30">
          <div className="grid grid-cols-2 gap-2 bg-slate-200/60 dark:bg-gray-700/60 p-1 rounded-xl">
            <button
              onClick={switchToHardware}
              className={`py-2 px-3 rounded-lg text-xs sm:text-sm font-semibold transition-all flex items-center justify-center space-x-1.5 ${
                scanMode === 'hardware'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-gray-600'
              }`}
            >
              <Keyboard className="h-3.5 w-3.5" />
              <span>USB Reader</span>
              {scanMode === 'hardware' && <CheckCircle className="h-3 w-3 ml-1" />}
            </button>

            <button
              onClick={switchToCamera}
              className={`py-2 px-3 rounded-lg text-xs sm:text-sm font-semibold transition-all flex items-center justify-center space-x-1.5 ${
                scanMode === 'camera'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-gray-600'
              }`}
            >
              <Camera className="h-3.5 w-3.5" />
              <span>Camera</span>
              {scanMode === 'camera' && <CheckCircle className="h-3 w-3 ml-1" />}
            </button>
          </div>
        </div>

        {/* Main Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-4">

          {error && (
            <div className="p-3 bg-rose-50 dark:bg-rose-900/20 rounded-xl border border-rose-200 dark:border-rose-800/60 text-rose-800 dark:text-rose-300 flex items-center text-xs font-semibold">
              <AlertCircle className="h-4 w-4 mr-2 shrink-0 text-rose-600" />
              <span>{error}</span>
            </div>
          )}

          {/* HARDWARE SCANNER MODE */}
          {scanMode === 'hardware' && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50/70 dark:bg-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-800/60 flex items-center space-x-3.5">
                <div className="p-2.5 bg-blue-600 text-white rounded-xl shrink-0">
                  <Keyboard className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white">Hardware Reader Active</h4>
                  <p className="text-[11px] text-slate-600 dark:text-slate-300 mt-0.5">
                    {hardwareActive ? 'Ready to receive scan.' : 'Waiting for scanner signal...'}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                  Or Type Code Manually
                </label>
                <form onSubmit={handleManualSubmit} className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={manualInput}
                    onChange={(e) => setManualInput(e.target.value)}
                    placeholder="Enter barcode string..."
                    className="flex-1 px-3.5 py-2.5 rounded-xl border border-slate-300 dark:border-gray-600 bg-slate-50 dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus={autoFocus}
                  />
                  <button
                    type="submit"
                    disabled={!manualInput.trim()}
                    className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-xl font-semibold transition-colors flex items-center justify-center shrink-0 shadow-sm"
                  >
                    <Scan className="h-4 w-4" />
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* CAMERA SCANNER MODE */}
          {scanMode === 'camera' && enableCamera && (
            <div className="space-y-4">
              <div className="relative bg-slate-900 rounded-2xl overflow-hidden aspect-square max-h-[300px] mx-auto border border-slate-700 shadow-inner flex items-center justify-center">

                {isLoading && !error && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-900 z-10">
                    <div className="text-center p-4">
                      <Loader className="h-8 w-8 text-purple-400 animate-spin mx-auto mb-2" />
                      <p className="text-white text-xs font-semibold">Starting camera feed...</p>
                    </div>
                  </div>
                )}

                {!error && (
                  <video
                    ref={videoRef}
                    muted
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                )}

                {/* Target Overlay Frame */}
                {!error && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                    <div className="relative w-44 h-44 border-2 border-dashed border-purple-400/50 rounded-xl">
                      <div className="absolute top-0 left-0 w-5 h-5 border-t-4 border-l-4 border-purple-500 rounded-tl-md"></div>
                      <div className="absolute top-0 right-0 w-5 h-5 border-t-4 border-r-4 border-purple-500 rounded-tr-md"></div>
                      <div className="absolute bottom-0 left-0 w-5 h-5 border-b-4 border-l-4 border-purple-500 rounded-bl-md"></div>
                      <div className="absolute bottom-0 right-0 w-5 h-5 border-b-4 border-r-4 border-purple-500 rounded-br-md"></div>
                      <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-purple-500/60 animate-pulse"></div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-center space-x-2">
                <button
                  onClick={toggleCamera}
                  className="px-3.5 py-2 bg-slate-100 dark:bg-gray-700 hover:bg-slate-200 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold transition-colors flex items-center space-x-1.5"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  <span>Flip Camera</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-4 border-t border-slate-100 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-900/30 flex justify-end">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 border border-slate-300 dark:border-gray-600 hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs sm:text-sm font-semibold transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanner;